import xss from 'xss';
import { getExtensionOfFilename } from './files';

export const sanitizeText = (input: string = ''): string => {
  if (!input) return '';

  const clean: string = xss(input);

  return clean;
};

export const removeHtml = (input: string = ''): string => {
  if (!input) return '';

  const sanitizedInput: string = sanitizeText(input);
  const clearInput: string = sanitizedInput.replace(/<\/?[^>]+>/gi, '');

  return clearInput;
};

/**
 * Converts HTML to plain text. For frontend text display use only.
 * Different from removeHtml because it preserves line breaks and multiple spaces.
 *
 * @param html - The HTML to convert to plain text
 * @returns The plain text representation of the HTML
 */
export const formatHtmlToPlainText = (html: string): string => {
  if (!html) return '';

  return sanitizeText(html)
    .replace(/<p>/g, '')
    .replace(/<\/p>/g, '\n\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/<[^>]*>/g, '')
    .trim();
};

/**
 * Sanitizes a file name by removing invalid characters and reducing consecutive periods.
 *
 * @param name - The new file name to sanitize (does not include extension)
 * @param originalName - The original file name (including extension)
 * @returns The final sanitized file name (including extension)
 */
export const sanitizeName = (
  name: string = '',
  originalName: string = '',
): string => {
  if (!name || !originalName) return '';

  const originalExtension: string = getExtensionOfFilename(originalName);
  const extension: string = originalExtension ? `.${originalExtension}` : '';

  return sanitizeS3Name(name).replace(/\.$/, '').concat(extension);
};

/**
 * Sanitizes a file name per S3 rules.
 *
 * @param name - The new file name to sanitize
 * @returns The sanitized file name
 */
export const sanitizeS3Name = (name: string = ''): string => {
  if (!name) return '';

  return name.trim().replace(/\.{2,}/g, '.');
};

/**
 * Validation for both file and folder names.
 */
export const validateName = (
  name: string = '',
): { isValid: boolean; error?: string } => {
  if (!name) return { isValid: false, error: 'Cannot be empty' };

  if (/[/]+/g.test(name)) {
    return {
      isValid: false,
      error: 'Name cannot contain forward slashes (/)',
    };
  }

  if (/[.][.]/g.test(name)) {
    return {
      isValid: false,
      error: 'Name cannot contain two or more consecutive dots (..)',
    };
  }

  if (name.endsWith('.')) {
    return {
      isValid: false,
      error: 'Name cannot end with a period (.)',
    };
  }

  return { isValid: true };
};

/**
 * Converts plain text to HTML. For backend text use only.
 *
 * @param plainText - The plain text to convert to HTML
 * @returns The HTML representation of the plain text
 */
export const formatPlainTextToHtmlForBackend = (plainText: string): string => {
  if (!plainText) return '';

  const paragraphs = plainText.split(/\n{2,}/);

  const html = paragraphs
    .map((paragraph) => {
      const withLineBreaks = paragraph.replace(/\n/g, '<br>');
      return `<p>${withLineBreaks}</p>`;
    })
    .join('');

  return sanitizeText(html);
};
